'use client';

import { motion } from 'framer-motion';
import Link from 'next/link';
import {
    Brain, Layers, Shield, Target, Cpu, ArrowRight, CheckCircle,
    Building2, Stethoscope, Factory, Scale, Landmark, Palette, Lightbulb, Monitor,
    Zap, Gem, Network, Coins, Combine, Sparkles, FolderSearch, FileSearch, Database, Component, FileJson
} from 'lucide-react';
import { useTheme } from '@/components/ThemeContext';
import dynamic from 'next/dynamic';
import DSLMConsole from '@/components/dslm/DSLMConsole';

const KnowledgeGraph3D = dynamic(() => import('@/components/KnowledgeGraph3D'), { ssr: false });

/* ═══ Persona Data ═══ */
const PERSONAS = [
    {
        icon: Building2,
        domain: '건축·설계',
        color: '#3b82f6',
        before: [
            'NAS에서 과거 도면 수동 검색 (평균 30분+)',
            '견적 기준·단가 매번 재조사',
            '설계 변경 시 전체 문서 수동 수정',
        ],
        after: [
            'AI가 프로젝트 유사도 분석 후 5초 내 추천',
            '학습된 단가 DB로 자동 견적 산출',
            '변경 사항 자동 전파 및 정합성 검증',
        ],
        improvement: 'BIM·CAD 데이터를 DSLM으로 구조화하여 설계 자동화 및 견적 정확도 95%+ 달성',
    },
    {
        icon: Factory,
        domain: '건설·시공',
        color: '#22c55e',
        before: [
            '시공 표준·규격 검색에 시간 소요',
            '하자 이력 관리 수기/엑셀 기반',
            '안전 매뉴얼 현장 적용 어려움',
        ],
        after: [
            'DSLM이 규격·법규를 즉시 참조·답변',
            'AI 기반 하자 패턴 분석 및 사전 예방',
            '현장 맞춤형 안전 체크리스트 자동 생성',
        ],
        improvement: 'CXD 데이터가 건설로봇(Physical AI) 명령어로 직접 활용, 시공 자동화 실현',
    },
    {
        icon: Landmark,
        domain: '공공기관·발주처',
        color: '#a855f7',
        before: [
            '기술 심사·평가 자료 수작업 비교',
            '과업지시서 작성 시 유사 사례 부족',
            '보안 우려로 AI 도입 불가',
        ],
        after: [
            'DSLM이 유사 프로젝트 자동 비교·분석',
            '학습된 과업 템플릿으로 초안 자동 생성',
            '온프레미스 배포로 보안 100% 보장',
        ],
        improvement: '폐쇄망 환경에서도 구축 가능한 온프레미스 DSLM으로 공공 데이터 보안 확보',
    },
    {
        icon: Palette,
        domain: '인테리어·디자인',
        color: '#f59e0b',
        before: [
            '과거 색채 계획·마감재 사례 재활용 어려움',
            '클라이언트 취향 파악에 시간 소요',
            '렌더링 수정 반복 작업',
        ],
        after: [
            'DSLM이 스타일 트렌드 분석 후 추천',
            '사용자 선호도 학습으로 맞춤 제안',
            'AI 기반 자동 렌더링 변이 생성',
        ],
        improvement: '설계 포트폴리오 데이터를 학습하여 디자인 의사결정 속도 3배 향상',
    },
    {
        icon: Scale,
        domain: '법률·계약',
        color: '#ef4444',
        before: [
            '계약서 조항 비교·검토 시간 과다',
            '법률 변경사항 추적 어려움',
            '분쟁 시 유사 판례 검색 수동',
        ],
        after: [
            'DSLM이 조항별 리스크 자동 분석',
            '관련 법규 변경 시 자동 알림',
            '학습된 판례 DB에서 즉시 유사 사례 제시',
        ],
        improvement: '건설 분쟁·계약 관련 법률 검토 시간 80% 단축',
    },
    {
        icon: Stethoscope,
        domain: '시설관리·FM',
        color: '#06b6d4',
        before: [
            '설비 이력 관리 엑셀/종이 기반',
            '고장 예측 불가, 사후 대응만 가능',
            '에너지 최적화 데이터 분석 부재',
        ],
        after: [
            'DSLM이 설비 이력 자동 분석·예측',
            'IoT 데이터 기반 사전 점검 자동 스케줄',
            'AI 에너지 사용 패턴 분석으로 15%+ 절감',
        ],
        improvement: '시설물 유지보수 이력을 DSLM으로 학습, 예방 정비 체계 구축',
    },
];

const STEPS = [
    {
        step: 1,
        title: '데이터 정제 (Data Cleaning)',
        desc: '유지보수가 잘 된 코드와 정확한 기술 문서를 선별하여 학습 데이터셋을 구축합니다.',
        icon: '🧹',
    },
    {
        step: 2,
        title: '역할 설정 (Role Definition)',
        desc: '모델이 코드 리뷰어인지, 아키텍트인지 명확한 페르소나를 부여합니다.',
        icon: '🎭',
    },
    {
        step: 3,
        title: '도메인 적응 (Domain Adaptation)',
        desc: '범용 모델을 기업 고유의 데이터·업무 프로세스에 맞게 Fine-tuning합니다.',
        icon: '🧠',
    },
    {
        step: 4,
        title: '검증 및 문서화',
        desc: '결과물이 실제 프로젝트 구조와 일치하는지 확인하고 유지보수 가이드를 생성합니다.',
        icon: '✅',
    },
];

export default function DSLMPage() {
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    return (
        <main className={`min-h-screen pt-16 ${isDark ? 'bg-gradient-to-b from-gray-950 via-[#0a0e1a] to-gray-950 text-white' : 'bg-gradient-to-b from-slate-50 via-white to-slate-100 text-gray-900'}`}>
            {/* Back Nav */}
            <div className="max-w-7xl mx-auto px-6 py-6">
                <Link href="/" className={`text-sm transition-colors flex items-center gap-1 ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}>
                    ← 메인으로 돌아가기
                </Link>
            </div>

            {/* HERO-LEVEL: Ontology Generation Visualization Section */}
            <section className="relative min-h-[90vh] flex flex-col justify-center w-full overflow-hidden border-y border-white/5 py-24">
                {/* 3D Knowledge Graph Animation */}
                <div className="absolute inset-0 z-0">
                    <KnowledgeGraph3D />
                </div>
                
                {/* Full-bleed Video Background (Fallback & Base Layer) */}
                <div className="absolute inset-0 z-0 select-none pointer-events-none mix-blend-screen opacity-40">
                    <video 
                        autoPlay loop muted playsInline 
                        className="w-full h-full object-cover"
                        style={{ filter: isDark ? 'brightness(0.3) saturate(1.5)' : 'brightness(0.7) saturate(1.2)' }}
                        src="/shorts-03-folder-to-db.mp4" 
                    />
                </div>
                {/* Gradient Overlays */}
                <div className={`absolute inset-0 z-0 pointer-events-none ${isDark ? 'bg-gradient-to-t from-gray-950 via-gray-950/60 to-gray-950/90' : 'bg-gradient-to-t from-slate-50 via-slate-50/60 to-slate-50/90'}`} />
                <div className={`absolute inset-x-0 bottom-0 h-48 z-0 pointer-events-none bg-gradient-to-t ${isDark ? 'from-gray-950' : 'from-slate-50'} to-transparent`} />

                <div className="relative z-10 max-w-7xl mx-auto px-6 w-full mt-10">
                    {/* Hero Title Area */}
                    <div className="text-center mb-24 relative">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
                            className="inline-flex items-center gap-3 rounded-full px-6 py-2.5 mb-8 backdrop-blur-md bg-white/5 border border-white/10 shadow-[0_0_30px_rgba(34,211,238,0.2)]"
                        >
                            <span className="relative flex h-3 w-3">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-3 w-3 bg-cyan-500"></span>
                            </span>
                            <span className={`text-sm md:text-base font-bold tracking-widest uppercase ${isDark ? 'text-cyan-300' : 'text-cyan-800'}`}>SnapAgent 3D Ontology Engine</span>
                        </motion.div>
                        
                        <motion.h2 
                            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
                            className={`text-4xl md:text-6xl lg:text-7xl font-black mb-8 tracking-tighter leading-[1.1] ${isDark ? 'text-white' : 'text-gray-900'}`}
                        >
                            세계 최고 수준의 <br className="hidden md:block"/>
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 drop-shadow-sm">건축 인테리어 도메인 온톨로지</span> <br className="hidden md:block"/>
                            자동 생성 시각화
                        </motion.h2>

                        <motion.p 
                            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
                            className={`text-lg md:text-2xl max-w-4xl mx-auto leading-relaxed font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
                        >
                            산재된 형태의 수만 장의 도면, 내역서, PDF가 AI 요원의 탐색을 통해 <br className="hidden lg:block"/>
                            <strong className={`inline-block px-3 py-1 rounded-xl border mt-2 ${isDark ? 'bg-white/10 border-white/20 text-white' : 'bg-blue-50 border-blue-200 text-blue-900'}`}>공간·자재·비용의 통합 3D 지식 그래프(Knowledge Graph)</strong>로 실시간 재조립됩니다.
                        </motion.p>
                    </div>

                    {/* Highly Visual Animated Sequence */}
                    <div className="relative">
                        {/* Animated Glowing Path Layer */}
                        <div className="absolute top-[40%] left-0 right-0 hidden lg:block z-0 pointer-events-none">
                            <svg className="w-full h-24 overflow-visible" preserveAspectRatio="none">
                                <motion.path
                                    d="M 50 50 Q 250 100, 450 50 T 850 50 T 1300 50"
                                    fill="transparent"
                                    stroke="url(#visual-gradient)"
                                    strokeWidth="4"
                                    strokeDasharray="10 10"
                                    initial={{ pathLength: 0, opacity: 0 }}
                                    whileInView={{ pathLength: 1, opacity: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 2, ease: "easeInOut" }}
                                />
                                <defs>
                                    <linearGradient id="visual-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                        <stop offset="0%" stopColor="#22d3ee" stopOpacity="0" />
                                        <stop offset="50%" stopColor="#3b82f6" stopOpacity="1" />
                                        <stop offset="100%" stopColor="#a855f7" stopOpacity="0" />
                                    </linearGradient>
                                </defs>
                            </svg>
                        </div>

                        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-8 relative z-10 w-full pb-10">
                            {[
                                { 
                                    step: '1', title: 'Raw Data 인입', 
                                    desc: '폴더에 산재된 비정형 문서를 Agent가 스캔합니다.', 
                                    example: '1층평면도.dwg, 최종내역.xlsx',
                                    emoji: '📂', glow: 'from-blue-500 to-cyan-400' 
                                },
                                { 
                                    step: '2', title: '개체(Entity) 추출', 
                                    desc: 'Gemini AI가 도면과 내역서 안의 핵심 객체를 분리합니다.', 
                                    example: '[대회의실], [텍스천장], [단가: 5만]',
                                    emoji: '🧠', glow: 'from-purple-500 to-pink-500' 
                                },
                                { 
                                    step: '3', title: '트리플(Triple) 매핑', 
                                    desc: '서로 다른 파일에서 뽑은 개체들을 관계선으로 묶어줍니다.', 
                                    example: '(대회의실) ➡️ 마감재 ➡️ (텍스천장)',
                                    emoji: '⛓️', glow: 'from-amber-400 to-orange-500' 
                                },
                                { 
                                    step: '4', title: '3D Graph 완성', 
                                    desc: '수만 개의 관계망이 형성되어 실시간 추론이 가능해집니다.', 
                                    example: 'Q: 대회의실 공사비는? A: 총 150만원',
                                    emoji: '🌌', glow: 'from-emerald-400 to-teal-500' 
                                },
                            ].map((item, idx) => (
                                <motion.div
                                    key={item.step}
                                    initial={{ opacity: 0, y: 60, scale: 0.9 }}
                                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                                    viewport={{ once: true, margin: "-100px" }}
                                    transition={{ delay: idx * 0.2 + 0.3, duration: 0.7, type: 'spring', bounce: 0.5 }}
                                    className="group relative"
                                >
                                    {/* Hover Glow Background */}
                                    <div className={`absolute -inset-0.5 bg-gradient-to-br ${item.glow} rounded-[2.5rem] blur-xl opacity-0 group-hover:opacity-40 transition duration-500`} />
                                    
                                    <div className={`relative h-full flex flex-col items-center text-center p-8 rounded-[2.5rem] border backdrop-blur-xl transition-all duration-500 ${isDark ? 'bg-gray-900/60 border-white/10 hover:bg-gray-800/80 hover:-translate-y-4 shadow-2xl' : 'bg-white/80 border-gray-200 hover:bg-white hover:-translate-y-4 shadow-xl shadow-gray-200/50'}`}>
                                        
                                        {/* Step Badge */}
                                        <div className={`absolute -top-5 left-1/2 -translate-x-1/2 w-12 h-12 rounded-2xl flex items-center justify-center font-black text-xl text-white shadow-xl bg-gradient-to-br ${item.glow} group-hover:scale-110 transition-transform`}>
                                            {item.step}
                                        </div>

                                        {/* Emoji Icon Container */}
                                        <div className="w-24 h-24 mt-4 rounded-full flex items-center justify-center border border-white/10 shadow-inner group-hover:shadow-[inset_0_0_30px_rgba(255,255,255,0.2)] transition-shadow overflow-hidden relative">
                                            <div className="absolute inset-0 bg-white/5 group-hover:bg-white/10 transition-colors" />
                                            <motion.span 
                                                className="text-[48px] drop-shadow-2xl z-10 block"
                                                animate={{ y: [0, -8, 0] }}
                                                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: idx * 0.5 }}
                                            >
                                                {item.emoji}
                                            </motion.span>
                                        </div>

                                        <h3 className={`text-xl font-black mt-6 mb-3 tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.title}</h3>
                                        <p className={`text-sm leading-relaxed font-medium mb-5 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{item.desc}</p>
                                        
                                        {/* Example Box (Makes Ontology Easy to Understand) */}
                                        <div className={`mt-auto w-full p-3 rounded-xl border ${isDark ? 'bg-black/40 border-cyan-500/20 text-cyan-200' : 'bg-blue-50/80 border-blue-200 text-blue-800'} text-[13px] font-mono tracking-tight text-left break-keep shadow-inner`}>
                                            <span className="opacity-60 text-[10px] uppercase block mb-1 font-bold">Action / Result</span>
                                            {item.example}
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Hero */}
            <section className="py-16 relative overflow-hidden">
                <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-r from-purple-900/20 via-blue-900/10 to-cyan-900/20' : 'bg-gradient-to-r from-purple-100/40 via-blue-100/20 to-cyan-100/40'}`} />
                <div className="max-w-5xl mx-auto px-6 relative z-10 text-center">
                    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
                        <div className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 mb-6 ${isDark ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-purple-100 border border-purple-200'}`}>
                            <Brain className={`w-4 h-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                            <span className={`text-sm font-medium ${isDark ? 'text-purple-300' : 'text-purple-700'}`}>Domain Specific Language Model</span>
                        </div>
                        <h1 className={`text-4xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent ${isDark ? 'bg-gradient-to-r from-white via-purple-100 to-blue-200' : 'bg-gradient-to-r from-gray-900 via-purple-700 to-blue-700'}`}>
                            DSLM
                        </h1>
                        <p className={`text-xl mb-4 max-w-3xl mx-auto ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                            귀사의 도메인 지식을 깊이 있게 학습한 <span className={`font-semibold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>전문 AI 모델</span>
                        </p>
                        <p className={`max-w-2xl mx-auto leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            일반 LLM이 &apos;범용 백과사전&apos;이라면, DSLM은 <strong className={isDark ? 'text-white' : 'text-gray-900'}>&apos;귀사 전문 기술자&apos;</strong>입니다.
                            기업 고유 데이터와 업무 프로세스를 이해하고, 코드 생성·문서 분석·자동화 로직을 수행합니다.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Core Features */}
            <section className="py-16 max-w-7xl mx-auto px-6">
                <motion.h2
                    initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
                    className={`text-3xl font-bold text-center mb-12 ${isDark ? 'text-white' : 'text-gray-900'}`}
                >
                    DSLM 핵심 특징
                </motion.h2>

                <div className="grid md:grid-cols-3 gap-6 mb-16">
                    {[
                        { icon: Target, title: '고도로 최적화된 문맥 이해', desc: '일반 모델이 놓치기 쉬운 기업 내부 전문 용어나 고유 프로젝트 구조를 정확히 파악합니다.', color: '#3b82f6' },
                        { icon: Layers, title: '유지보수 효율성 (레고 원칙)', desc: '기능을 레고 블록처럼 모듈화. 코드 발생 지점과 오류 가능성을 직관적으로 파악 가능하도록 설계.', color: '#22c55e' },
                        { icon: Shield, title: '보안 및 독립성', desc: '외부 유출이 민감한 내부 데이터를 학습. 폐쇄망 환경에서도 온프레미스로 높은 성능을 발휘합니다.', color: '#a855f7' },
                    ].map((feat, i) => (
                        <motion.div
                            key={feat.title}
                            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.1 }} viewport={{ once: true }}
                            className={`p-6 rounded-2xl border transition-all ${isDark ? 'border-white/10 bg-white/[0.02] hover:border-white/20' : 'border-gray-200 bg-white hover:border-gray-300 shadow-sm'}`}
                        >
                            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: `${feat.color}${isDark ? '15' : '18'}` }}>
                                <feat.icon className="w-6 h-6" style={{ color: feat.color }} />
                            </div>
                            <h3 className={`font-bold text-lg mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{feat.title}</h3>
                            <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{feat.desc}</p>
                        </motion.div>
                    ))}
                </div>

                {/* Key Terms */}
                <motion.div
                    initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
                    className={`rounded-2xl border p-8 mb-16 ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-white shadow-sm'}`}
                >
                    <h3 className={`text-xl font-bold mb-6 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        <Cpu className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} /> 주요 용어
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                        {[
                            { term: 'Domain Adaptation', desc: '범용 모델을 특정 도메인(예: 건축설계, 시공관리)에 맞게 조정하는 과정' },
                            { term: 'Knowledge Graph', desc: '도메인 내 개념 간 관계를 네트워크로 구조화. DSLM의 논리적 추론 지도' },
                            { term: 'Fine-tuning', desc: '사전 학습 모델에 도메인 데이터를 추가 학습시켜 파라미터를 미세 조정' },
                            { term: 'Context Window', desc: '한 번에 처리할 수 있는 정보량. 긴 코드 스택이나 매뉴얼 분석을 효율적으로 관리' },
                            { term: 'Tokenization', desc: '도메인 특화 용어를 모델이 이해할 수 있는 최소 단위로 분할하는 과정' },
                            { term: 'RAG (검색 증강)', desc: '외부 문서를 실시간 검색하여 모델 응답에 반영. 최신 정보 반영에 핵심' },
                        ].map(item => (
                            <div key={item.term} className={`p-4 rounded-xl border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50 border-gray-100'}`}>
                                <span className={`text-sm font-bold ${isDark ? 'text-blue-300' : 'text-blue-600'}`}>{item.term}</span>
                                <p className={`text-xs mt-1 leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </motion.div>
            </section>

            {/* Persona-based Before/After */}
            <section className={`py-16 ${isDark ? 'bg-gradient-to-b from-purple-950/10 to-transparent' : 'bg-gradient-to-b from-purple-50/50 to-transparent'}`}>
                <div className="max-w-7xl mx-auto px-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                        className="text-center mb-14"
                    >
                        <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>분야별 DSLM 도입 효과</h2>
                        <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>각 분야에 맞는 페르소나를 적용하여 Before→After 개선 포인트를 확인하세요</p>
                    </motion.div>

                    <div className="grid lg:grid-cols-2 gap-6">
                        {PERSONAS.map((persona, idx) => (
                            <motion.div
                                key={persona.domain}
                                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.08 }} viewport={{ once: true }}
                                className={`rounded-2xl border overflow-hidden transition-all ${isDark ? 'border-white/10 bg-white/[0.02] hover:border-white/20' : 'border-gray-200 bg-white hover:border-gray-300 shadow-sm'}`}
                            >
                                {/* Header */}
                                <div className={`px-6 py-4 border-b flex items-center gap-3 ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${persona.color}${isDark ? '15' : '18'}` }}>
                                        <persona.icon className="w-5 h-5" style={{ color: persona.color }} />
                                    </div>
                                    <h3 className={`font-bold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>{persona.domain}</h3>
                                </div>

                                <div className="p-6 grid md:grid-cols-2 gap-4">
                                    {/* Before */}
                                    <div>
                                        <span className={`text-xs font-bold px-2 py-1 rounded ${isDark ? 'text-red-400 bg-red-500/10' : 'text-red-600 bg-red-50 border border-red-200'}`}>BEFORE</span>
                                        <ul className="mt-3 space-y-2">
                                            {persona.before.map(item => (
                                                <li key={item} className={`text-xs flex items-start gap-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
 <span className={`mt-0.5 ${isDark ? 'text-red-400' : 'text-red-500'}`}></span>{item}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    {/* After */}
                                    <div>
                                        <span className={`text-xs font-bold px-2 py-1 rounded ${isDark ? 'text-green-400 bg-green-500/10' : 'text-green-700 bg-green-50 border border-green-200'}`}>AFTER (DSLM)</span>
                                        <ul className="mt-3 space-y-2">
                                            {persona.after.map(item => (
                                                <li key={item} className={`text-xs flex items-start gap-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                                    <CheckCircle className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${isDark ? 'text-green-400' : 'text-green-600'}`} /> {item}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>

                                {/* Improvement */}
                                <div className="px-6 pb-5">
                                    <div className={`p-3 rounded-lg border ${isDark ? 'bg-gradient-to-r from-blue-500/5 to-purple-500/5 border-blue-500/10' : 'bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200'}`}>
 <p className={`text-xs leading-relaxed ${isDark ? 'text-blue-300' : 'text-blue-700'}`}><Lightbulb className="w-4 h-4 inline-block" />{persona.improvement}</p>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Implementation Steps */}
            <section className="py-16 max-w-5xl mx-auto px-6">
                <motion.h2
                    initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
                    className={`text-3xl font-bold text-center mb-12 ${isDark ? 'text-white' : 'text-gray-900'}`}
                >
                    DSLM 구축 4단계
                </motion.h2>

                <div className="space-y-4">
                    {STEPS.map((s, i) => (
                        <motion.div
                            key={s.step}
                            initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.1 }} viewport={{ once: true }}
                            className={`flex items-start gap-5 p-5 rounded-2xl border transition-all ${isDark ? 'border-white/10 bg-white/[0.02] hover:border-white/20' : 'border-gray-200 bg-white hover:border-gray-300 shadow-sm'}`}
                        >
                            <div className="text-3xl">{s.icon}</div>
                            <div>
                                <div className="flex items-center gap-3 mb-1">
                                    <span className={`text-xs font-bold px-2 py-0.5 rounded ${isDark ? 'text-purple-400 bg-purple-500/10' : 'text-purple-700 bg-purple-100 border border-purple-200'}`}>STEP {s.step}</span>
                                    <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{s.title}</h3>
                                </div>
                                <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{s.desc}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Concrete Task Examples */}
            <section className="py-16 max-w-5xl mx-auto px-6">
                <motion.h2
                    initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
                    className={`text-3xl font-bold text-center mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}
                >
                    DSLM이 수행하는 실제 업무
                </motion.h2>
                <p className={`text-center mb-10 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>구매 담당자가 바로 이해할 수 있는 업무 단위 예시 10가지</p>

                <div className="grid md:grid-cols-2 gap-3">
                    {[
 { emoji: '🔗', task: '내역서 ↔ 도면 자동 매핑', desc: '도면 번호를 인식하여 관련 내역서·스펙을 자동 연결. 수동 대조 시간 90% 절감.' },
 { emoji: '📁', task: '프로젝트 폴더 자동 표준화', desc: 'NAS에 산재된 비정형 폴더 구조를 기업 표준 분류체계로 자동 재조직.' },
 { emoji: '📋', task: '입찰 서류 체크리스트 자동 생성', desc: '과업지시서를 분석하여 필수 제출 서류 목록과 누락 항목을 자동 도출.' },
 { emoji: '🕵️‍♀️', task: '설계 변경 이력/감사 로그 생성', desc: '도면 변경 시 어떤 항목이 바뀌었는지, 영향받는 다른 문서를 자동 추적·기록.' },
 { emoji: '💰', task: '견적서 자동 산출 (SnapPrice)', desc: '구조화된 데이터 기반으로 공사비를 자동 산출. 기존 45분 → 1분 이내 완료.' },
 { emoji: '🔍', task: '유사 프로젝트 자동 검색/추천', desc: '"이전에 비슷한 규모의 오피스텔 프로젝트가 있었나?" AI가 유사도 분석 후 5초 내 추천.' },
 { emoji: '⚡', task: '회의록 → 액션 아이템 자동 추출', desc: '설계 회의 녹취/텍스트에서 결정사항·담당자·기한을 자동 정리.' },
 { emoji: '⚖️', task: '법규/인허가 자동 체크', desc: '설계안이 관련 건축법규·소방법·에너지법을 충족하는지 자동 검증.' },
 { emoji: '📊', task: '프로젝트 진행률 자동 보고서', desc: '데이터 기반으로 주간/월간 진행 현황 보고서를 자동 생성하여 이메일 발송.' },
 { emoji: '💬', task: '사내 AI 챗봇 (RAG 기반)', desc: '"3층 화장실 마감재가 뭐였지?" 같은 질문에 사내 문서를 검색하여 즉시 답변.' },
                    ].map((item, i) => (
                        <motion.div
                            key={item.task}
                            initial={{ opacity: 0, x: i % 2 === 0 ? -15 : 15 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.05 }}
                            className={`flex items-start gap-3 p-4 rounded-xl border transition-all ${isDark ? 'border-white/10 bg-white/[0.02] hover:border-purple-500/30' : 'border-gray-200 bg-white hover:border-purple-300 shadow-sm'}`}
                        >
                            <div className="text-xl flex-shrink-0 mt-0.5">{item.emoji}</div>
                            <div>
                                <h4 className={`text-sm font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.task}</h4>
                                <p className={`text-[11px] leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{item.desc}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>


            {/* SnapGRID Data Ontology Advantage */}
            <section className={`py-16 relative overflow-hidden ${isDark ? 'bg-gradient-to-b from-gray-950 via-indigo-950/20 to-gray-950' : 'bg-gradient-to-b from-slate-50 via-indigo-50/50 to-slate-50'}`}>
                <div className="max-w-6xl mx-auto px-6 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <div className={`inline-flex items-center gap-2 rounded-full px-5 py-2 mb-5 ${isDark ? 'bg-indigo-500/10 border border-indigo-500/20' : 'bg-indigo-100 border border-indigo-200 shadow-sm'}`}>
                            <Gem className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
                            <span className={`text-sm font-bold tracking-wide ${isDark ? 'text-indigo-300' : 'text-indigo-700'}`}>세계 최초 K-건축·인테리어 멀티모달 온톨로지</span>
                        </div>
                        <h2 className={`text-3xl md:text-5xl font-bold mb-5 tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            SnapGRID 데이터셋이 <span className={isDark ? 'text-indigo-400' : 'text-indigo-600'}>독보적인 4가지 이유</span>
                        </h2>
                        <p className={`max-w-3xl mx-auto text-base md:text-lg leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            에이전트가 현실 현장의 산재된 비정형 데이터(CAD, Excel, PDF, 도면 이미지)를 <br className="hidden md:block"/>
                            스스로 순회하며 직접 <strong className={isDark ? 'text-white' : 'text-gray-900'}>&apos;지식 그래프(Knowledge Graph)&apos;</strong>로 구축해 내는 혁신적인 솔루션입니다.
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 gap-8">
                        {[
                            {
                                icon: Combine,
                                title: '초극강의 파편화 극복 (Silo-Breaker)',
                                color: '#f43f5e', // rose
                                beforeLabel: '분산성',
                                beforeText: '도면은 CAD(DWG), 내역서는 Excel, 시방서는 PDF 등 데이터가 완전히 분절되어 있으며 기존 BIM조차 특정 포맷(IFC, Revit)에 종속적입니다.',
                                afterLabel: '성취: 모달리티 통합',
                                afterText: '멀티모달 에이전트가 포맷에 구애받지 않고 스스로 파일을 열어, 이를 하나의 통합된 노드(위상) 구조로 조립·결합합니다.'
                            },
                            {
                                icon: Brain, 
                                title: '현업(Real-World) 용어와 표준의 결합',
                                color: '#eab308', // yellow
                                beforeLabel: '데이터 혼재',
                                beforeText: '"석고보드 위 VP도장", "10T 데코타일", "알루미늄ルー버(루버)" 등, 표준 규격과 일어 베이스 현장 은어가 혼재되어 활용이 불가능했습니다.',
                                afterLabel: '성취: 숨쉬는 데이터',
                                afterText: '날것의 시공 용어 원본은 유지하되, LLM이 이것을 수장공사, 도장공사 등 표준 Category 옷장에 스스로 분류하여 리얼 월드 데이터셋을 구축합니다.'
                            },
                            {
                                icon: Network,
                                title: '공간 중심의 위상 연결망 (Topology Mapping)',
                                color: '#06b6d4', // cyan
                                beforeLabel: '수동 작업의 한계',
                                beforeText: '기존엔 "A자재가 B공간 바닥에 들어간다"는 핵심 관계(Relationship)를 생성하기 위해 BIM 파라미터 수동 입력에 많은 인력이 투입해야 했습니다.',
                                afterLabel: '성취: 자동 관계 추출',
                                afterText: '도면 텍스트와 내역서, CG 이미지를 분석해 [공간]-[:HAS_FINISH {part:"바닥"}]->[마감재] 형태의 네트워크망을 자동 (Auto-Extraction) 생성합니다.'
                            },
                            {
                                icon: Coins,
                                title: '위상 데이터 기반 단가와 스펙의 융합',
                                color: '#10b981', // emerald
                                beforeLabel: '비용의 단절',
                                beforeText: '산출 및 단가 데이터는 도면 등 물리 세계 모델과 항상 이원화되어 있어 "5D BIM(3D + 공정 + 비용)" 구축은 건설업계 최고 난제였습니다.',
                                afterLabel: '성취: Data-driven 5D',
                                afterText: '내역서상의 조달 단가와 시방서 규격, 모델링 물량이 한 그래프 중심에서 조우하며, 크로스 레퍼런스로 즉각적인 비용 피드백 체계를 갖춥니다.'
                            }
                        ].map((reason, idx) => (
                            <motion.div
                                key={reason.title}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.15 }}
                                className={`flex flex-col p-8 rounded-2xl border transition-all ${isDark ? 'border-white/10 bg-white/[0.03] hover:border-indigo-500/30' : 'border-gray-200 bg-white hover:border-indigo-300 shadow-xl shadow-gray-200/50'}`}
                            >
                                <div className="flex items-center gap-4 mb-6">
                                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 shadow-inner`} style={{ background: `${reason.color}${isDark ? '20' : '15'}`, border: `1px solid ${reason.color}30` }}>
                                        <reason.icon className="w-7 h-7" style={{ color: reason.color }} />
                                    </div>
                                    <h3 className={`text-xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>{reason.title}</h3>
                                </div>
                                
                                <div className="flex flex-col gap-3 mt-auto flex-1">
                                    {/* AS-IS (Before) */}
                                    <div className={`p-5 rounded-2xl border flex-1 ${isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-gray-50 border-gray-200/60'}`}>
                                        <div className={`text-xs font-bold mb-2 uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{reason.beforeLabel}</div>
                                        <p className={`text-[13px] leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{reason.beforeText}</p>
                                    </div>
                                    
                                    {/* TO-BE (After) */}
                                    <div className={`p-5 flex gap-0 rounded-2xl border relative overflow-hidden flex-1 ${isDark ? 'border-indigo-500/20 bg-indigo-500/5' : 'border-indigo-200 bg-indigo-50'}`}>
                                        <div className="absolute top-0 left-0 w-1.5 h-full" style={{ backgroundColor: reason.color }} />
                                        <div className="pl-2">
                                            <div className="flex items-center gap-1.5 mb-2">
                                                <Zap className="w-4 h-4" style={{ color: reason.color }} />
                                                <div className="text-xs font-bold uppercase tracking-wider" style={{ color: reason.color }}>{reason.afterLabel}</div>
                                            </div>
                                            <p className={`text-[13px] leading-relaxed font-medium ${isDark ? 'text-indigo-200' : 'text-indigo-900'}`}>{reason.afterText}</p>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing */}
            <section className="py-16 max-w-6xl mx-auto px-6">
                <motion.h2
                    initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
                    className={`text-3xl font-bold text-center mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}
                >
                    DSLM 구축 비용
                </motion.h2>
                <p className={`text-center mb-12 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>귀사 규모와 데이터 복잡도에 따라 최적의 플랜을 선택하세요</p>

                <div className="grid md:grid-cols-3 gap-6 mb-12">
                    {[
                        {
                            plan: 'Starter',
                            price: '1,200',
                            unit: '만원~',
                            period: '일회성',
                            color: '#3b82f6',
                            desc: '소규모 팀 / 단일 도메인',
                            features: [
                                '단일 도메인 Fine-tuning',
                                '데이터 정제 + 학습 (최대 5만건)',
                                'Knowledge Graph 기본 구축',
                                'Cloud 기반 배포',
                                '도입 컨설팅 1회',
                                '기본 유지보수 가이드 제공',
                            ],
                            timeline: '4~6주',
                        },
                        {
                            plan: 'Professional',
                            price: '2,800',
                            unit: '만원~',
                            period: '일회성',
                            color: '#a855f7',
                            popular: true,
                            desc: '중대형 기업 / 복합 도메인',
                            features: [
                                '복합 도메인 Fine-tuning (3개까지)',
                                '데이터 정제 + 학습 (최대 30만건)',
                                'Knowledge Graph 고급 구축',
                                '하이브리드 (Cloud + On-Premise) 배포',
                                'RAG 검색 증강 통합',
                                '페르소나 역할 설정 (최대 5개)',
                                '분기별 모델 업데이트 1년',
                            ],
                            timeline: '6~10주',
                        },
                        {
                            plan: 'Enterprise',
                            price: '별도 협의',
                            unit: '',
                            period: '',
                            color: '#f59e0b',
                            desc: '대기업 / 공공기관 / 폐쇄망',
                            features: [
                                '무제한 도메인 Fine-tuning',
                                '대규모 데이터 학습 (제한 없음)',
                                '완전 온프레미스 배포 (폐쇄망 지원)',
                                '전용 GPU 서버 세팅',
                                'CXD 파이프라인 + 건설로봇 연계',
                                '상시 기술 지원 + SLA 보장',
                                '연간 모델 업데이트 + 신규 기능',
                            ],
                            timeline: '8~16주',
                        },
                    ].map((p) => (
                        <motion.div
                            key={p.plan}
                            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className={`relative rounded-2xl border overflow-hidden transition-all hover:scale-[1.02] ${p.popular
                                ? isDark ? 'border-purple-500/50 shadow-lg shadow-purple-500/10 bg-white/[0.02]' : 'border-purple-400 shadow-lg shadow-purple-200/40 bg-white'
                                : isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-white shadow-sm'
                                }`}
                        >
                            {p.popular && (
                                <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-center py-1.5 text-xs font-bold text-white">
                                    MOST POPULAR
                                </div>
                            )}
                            <div className="p-6">
                                <h3 className="text-lg font-bold mb-1" style={{ color: p.color }}>{p.plan}</h3>
                                <p className={`text-xs mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{p.desc}</p>
                                <div className="mb-5">
                                    <span className={`text-3xl font-black ${isDark ? 'text-white' : 'text-gray-900'}`}>{p.price}</span>
                                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{p.unit} {p.period}</span>
                                </div>
                                <div className={`flex items-center gap-2 mb-5 text-xs rounded-lg px-3 py-2 ${isDark ? 'text-gray-400 bg-white/[0.03]' : 'text-gray-500 bg-gray-50 border border-gray-100'}`}>
                                    <span>⏱️</span> 구축 기간: <strong className={isDark ? 'text-white' : 'text-gray-900'}>{p.timeline}</strong>
                                </div>
                                <ul className="space-y-2.5">
                                    {p.features.map(f => (
                                        <li key={f} className={`text-xs flex items-start gap-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                            <CheckCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: p.color }} />
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </motion.div>
                    ))}
                </div>

                <motion.div
                    initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
                    className={`p-5 rounded-xl border text-center ${isDark ? 'bg-gradient-to-r from-blue-500/5 to-purple-500/5 border-blue-500/10' : 'bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200'}`}
                >
                    <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
 <Lightbulb className="w-4 h-4 inline-block" /><strong className={isDark ? 'text-white' : 'text-gray-900'}>월 유지보수 비용:</strong>Starter 30만원/월 | Pro 80만원/월 | Enterprise 별도 SLA
                    </p>
                    <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        모든 플랜은 초기 데이터 분석 및 컨설팅 비용을 포함합니다. 정확한 견적은 상담 후 제공됩니다.
                    </p>
                </motion.div>
            </section>
            {/* SnapCodex + DSLM Architecture */}
            <section className="py-16 max-w-5xl mx-auto px-6">
                <motion.div
                    initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
                    className={`rounded-2xl border p-8 ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-white shadow-sm'}`}
                >
                    <h3 className={`text-xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>스냅코덱스 × DSLM 아키텍처</h3>
                    <div className="flex flex-wrap justify-center gap-3 mb-6">
                        {[
 { label: '설계 자산 수집', emoji: '📥' },
 { label: 'Knowledge Graph', emoji: '🕸️' },
 { label: 'DSLM Fine-tuning', emoji: '🎯' },
 { label: 'RAG 검색 증강', emoji: '🔍' },
 { label: 'CXD 출력', emoji: '📤' },
 { label: '건설로봇 연계', emoji: '🤖' },
                        ].map((item, i) => (
                            <div key={item.label} className="flex items-center gap-2">
                                <div className={`rounded-xl px-4 py-3 text-center border ${isDark ? 'bg-gray-800 border-white/5' : 'bg-gray-50 border-gray-200'}`}>
                                    <div className="text-xl mb-1">{item.emoji}</div>
                                    <div className={`text-[10px] ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{item.label}</div>
                                </div>
                                {i < 5 && <ArrowRight className={`w-4 h-4 hidden sm:block ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />}
                            </div>
                        ))}
                    </div>

                    <div className="grid md:grid-cols-3 gap-4 mt-6">
                        <div className={`p-4 rounded-xl border ${isDark ? 'bg-blue-500/5 border-blue-500/10' : 'bg-blue-50 border-blue-200'}`}>
                            <h4 className={`text-sm font-bold mb-2 ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>모듈화 (Modularity)</h4>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>레고 블록처럼 분리된 기능. 각 블록의 역할과 연결 부위를 명확히 정의합니다.</p>
                        </div>
                        <div className={`p-4 rounded-xl border ${isDark ? 'bg-green-500/5 border-green-500/10' : 'bg-green-50 border-green-200'}`}>
                            <h4 className={`text-sm font-bold mb-2 ${isDark ? 'text-green-300' : 'text-green-700'}`}>가이드 자동 생성</h4>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>개발 완료 시 DSLM이 구조와 유지보수 포인트를 자동 문서화합니다.</p>
                        </div>
                        <div className={`p-4 rounded-xl border ${isDark ? 'bg-amber-500/5 border-amber-500/10' : 'bg-amber-50 border-amber-200'}`}>
                            <h4 className={`text-sm font-bold mb-2 ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>오류 역추적</h4>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>도메인 논리를 바탕으로 문제 근원지를 즉각 제시합니다.</p>
                        </div>
                    </div>
                </motion.div>
            </section>

            {/* DSLM Live Console */}
            <section className="py-16 bg-gray-950 px-4">
                <DSLMConsole />
            </section>

            {/* CTA */}
            <section className="py-20 text-center">
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                    <h2 className={`text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>DSLM으로 귀사의 AI를 시작하세요</h2>
                    <p className={`mb-8 max-w-xl mx-auto ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        NAS에 잠들어 있는 데이터가 귀사만의 전문 AI가 됩니다.<br />
                        이미 선택이 아니라 <strong className={isDark ? 'text-white' : 'text-gray-900'}>필수</strong>입니다.
                    </p>
                    <div className="flex flex-wrap gap-4 justify-center">
                        <Link
                            href="/"
                            className="px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 rounded-xl font-semibold transition-all shadow-lg shadow-purple-600/25 flex items-center gap-2 text-white"
                        >
                            무료 상담 신청 <ArrowRight className="w-5 h-5" />
                        </Link>
                        <Link
                            href="/grid"
                            className={`px-8 py-4 border rounded-xl font-semibold transition-all flex items-center gap-2 ${isDark ? 'bg-green-500/10 hover:bg-green-500/20 border-green-500/20 text-green-300' : 'bg-green-50 hover:bg-green-100 border-green-200 text-green-700'}`}
                        >
 <Monitor className="w-4 h-4 inline-block" />Grid 대시보드
                        </Link>
                        <Link
                            href="/"
                            className={`px-8 py-4 border rounded-xl font-semibold transition-all ${isDark ? 'bg-white/5 hover:bg-white/10 border-white/10 text-white' : 'bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-700'}`}
                        >
                            메인 페이지로 돌아가기
                        </Link>
                    </div>
                </motion.div>
            </section>

            {/* Footer */}
            <footer className={`py-8 border-t text-center text-xs ${isDark ? 'border-white/5 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
                © 2026 Desinsight. All rights reserved. | SnapCodex 특허 제10-2890450호
            </footer>
        </main>
    );
}
